import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { financePayableItinerarySummaryQueryKey, financePayableQueryKey } from "../config";
import {
  fetchFinancePayablePrepaymentSummary,
  fetchFinancePayableSettlementSummary,
  fetchFinanceReceivableSummary,
  locateFinanceReceivableOrdersByProjectId,
  locateFinanceReceivableOrdersByTeamId,
  searchFinancePayableItineraries,
  searchFinanceReceivableProjects,
} from "../repo";
import { locateFinancePrepaymentItinerariesByTeamId } from "../repo/financePaymentRequestRepo";
import type { FinanceReceivableProjectSearchScope } from "../types";

const financeReceivableQueryRootKey = ["finance", "receivable"] as const;

/**
 * 应付清单页：搜团查询与选中团的应付汇总查询；选中哪个团由调用方（地址）决定。
 * canLocateTeam 为真（能发起请款）时，确认件里按关键字搜不到，再拿关键字当团编码精确找一次，
 * 这样还没有确认件的团也能打开、发起预付款。
 */
export function useFinancePayableListRuntime(itineraryId: string | null, canLocateTeam = false) {
  const [keyword, setKeyword] = useState("");
  const [submittedKeyword, setSubmittedKeyword] = useState("");

  const search = useQuery({
    queryKey: [...financePayableQueryKey, "itinerary-search", submittedKeyword],
    queryFn: () => searchFinancePayableItineraries(submittedKeyword),
    enabled: submittedKeyword.length > 0,
    retry: false,
  });

  const teamLocateEnabled = canLocateTeam && submittedKeyword.length > 0 && search.data?.items.length === 0;
  const teamLocate = useQuery({
    queryKey: [...financePayableQueryKey, "team-locate", submittedKeyword],
    queryFn: () => locateFinancePrepaymentItinerariesByTeamId(submittedKeyword),
    enabled: teamLocateEnabled,
    retry: false,
  });

  const summary = useQuery({
    queryKey: financePayableItinerarySummaryQueryKey(itineraryId),
    queryFn: () => fetchFinancePayableSettlementSummary(itineraryId as string),
    enabled: Boolean(itineraryId),
    retry: false,
  });

  return {
    keyword,
    setKeyword,
    /** 提交关键字；空白关键字等于收起搜索结果。 */
    submitSearch: () => setSubmittedKeyword(keyword.trim()),
    /** 选中团之后收起搜索结果，关键字保留在输入框里。 */
    closeSearch: () => setSubmittedKeyword(""),
    searchOpen: submittedKeyword.length > 0,
    search,
    /** 确认件里搜不到时按团编码找到的团；teamLocateEnabled 为假表示没有去找。 */
    teamLocateEnabled,
    teamLocate,
    summary,
  };
}

/**
 * 团还没有确认件时的预付款汇总（团头取行程本身）。queryKey 挂在这个团的应付汇总下面，
 * 提交请款后按应付汇总定向刷新时会一起刷新。
 */
export function useFinancePayablePrepaymentSummary(itineraryId: string) {
  return useQuery({
    queryKey: [...financePayableItinerarySummaryQueryKey(itineraryId), "prepayment"],
    queryFn: () => fetchFinancePayablePrepaymentSummary(itineraryId),
    retry: false,
  });
}

/**
 * 应收清单页：按团编码定位订单、按项目关键字搜项目再列它的订单，与选中团的应收汇总查询；
 * 选中哪个团由调用方（地址）决定。团编码唯一，查到一个订单就直接交给调用方打开；选中的项目
 * 只有一个订单时同样直接打开，多个时由页面列出来让人选。projectSearchScope 为空表示没有项目搜索。
 */
export function useFinanceReceivableListRuntime(
  orderId: string | null,
  onLocateOrder: (orderId: string) => void,
  projectSearchScope: FinanceReceivableProjectSearchScope | null,
) {
  const [teamId, setTeamId] = useState("");
  const [submittedTeamId, setSubmittedTeamId] = useState("");

  const locate = useQuery({
    queryKey: [...financeReceivableQueryRootKey, "locate", submittedTeamId],
    queryFn: () => locateFinanceReceivableOrdersByTeamId(submittedTeamId),
    enabled: submittedTeamId.length > 0,
    retry: false,
  });
  const locatedOrderId = locate.data?.length === 1 ? locate.data[0].orderId : null;

  useEffect(() => {
    if (locatedOrderId === null) return;
    setSubmittedTeamId("");
    onLocateOrder(locatedOrderId);
  }, [locatedOrderId, onLocateOrder]);

  const [projectKeyword, setProjectKeyword] = useState("");
  const [submittedProjectKeyword, setSubmittedProjectKeyword] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const projectSearch = useQuery({
    queryKey: [...financeReceivableQueryRootKey, "project-search", projectSearchScope, submittedProjectKeyword],
    queryFn: () =>
      searchFinanceReceivableProjects(submittedProjectKeyword, projectSearchScope as FinanceReceivableProjectSearchScope),
    enabled: projectSearchScope !== null && submittedProjectKeyword.length > 0,
    retry: false,
  });

  const projectOrders = useQuery({
    queryKey: [...financeReceivableQueryRootKey, "project-orders", selectedProjectId],
    queryFn: () => locateFinanceReceivableOrdersByProjectId(selectedProjectId as string),
    enabled: selectedProjectId !== null,
    retry: false,
  });
  const projectOrderId = projectOrders.data?.length === 1 ? projectOrders.data[0].orderId : null;

  useEffect(() => {
    if (projectOrderId === null) return;
    setSelectedProjectId(null);
    onLocateOrder(projectOrderId);
  }, [projectOrderId, onLocateOrder]);

  const summary = useQuery({
    queryKey: [...financeReceivableQueryRootKey, "summary", orderId],
    queryFn: () => fetchFinanceReceivableSummary(orderId as string),
    enabled: Boolean(orderId),
    retry: false,
  });

  return {
    teamId,
    setTeamId,
    /** 提交团编码；空白等于收起定位结果。同时收起项目搜索留下的结果和提示。 */
    submitTeamId: () => {
      setSubmittedProjectKeyword("");
      setSelectedProjectId(null);
      setSubmittedTeamId(teamId.trim());
    },
    locateOpen: submittedTeamId.length > 0,
    locate,
    projectKeyword,
    setProjectKeyword,
    /** 提交项目关键字；空白等于收起搜索结果。同时收起上一个项目的订单和团编码定位留下的提示。 */
    submitProjectSearch: () => {
      setSubmittedTeamId("");
      setSelectedProjectId(null);
      setSubmittedProjectKeyword(projectKeyword.trim());
    },
    projectSearchOpen: projectSearchScope !== null && submittedProjectKeyword.length > 0,
    projectSearch,
    /** 选中一个项目：收起搜索结果，去查它的订单。 */
    selectProject: (projectId: string) => {
      setSubmittedProjectKeyword("");
      setSelectedProjectId(projectId);
    },
    projectOrdersOpen: selectedProjectId !== null,
    projectOrders,
    /** 从项目的多个订单里选一个打开。 */
    openProjectOrder: (projectOrderIdToOpen: string) => {
      setSelectedProjectId(null);
      onLocateOrder(projectOrderIdToOpen);
    },
    summary,
  };
}
